<template>
  <ion-page>
    <PageHeaderWithMenu title="Музыка" :scrolled="headerScrolled" />
    <ion-content
      :class="['music-library-content', isMobile ? 'main-background' : 'liga-web-background']"
      :fullscreen="true"
      @ionScroll="onContentScroll"
    >
      <ion-refresher slot="fixed" @ionRefresh="loadFolders($event)">
        <ion-refresher-content />
      </ion-refresher>
      <ion-list v-if="!loading && folders.length" class="music-library-list" lines="none">
        <ion-item
          v-for="f in folders"
          :key="f.id"
          button
          class="music-library-folder-item"
          @click="$router.push(`/music-library/${f.id}`)"
        >
          <ion-label>
            <h2>{{ f.title }}</h2>
          </ion-label>
          <ion-icon slot="end" :icon="chevronForwardOutline" class="music-library-chevron" />
        </ion-item>
      </ion-list>
      <p v-else-if="!loading" class="ion-padding empty-hint music-library-empty">Нет доступных папок.</p>
      <ion-spinner v-if="loading" class="ion-margin music-library-spinner" name="crescent" />
    </ion-content>
  </ion-page>
</template>

<script lang="ts">
import PageHeaderWithMenu from '@/components/PageHeaderWithMenu.vue';
import { GlobalContext } from '@/model/globalcontext.model';
import { ILibraryMusicFolder, libraryMusicService } from '@/services/library-music.service';
import {
  IonContent,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  IonSpinner,
} from '@ionic/vue';
import { chevronForwardOutline } from 'ionicons/icons';
import { defineComponent, inject } from 'vue';

export default defineComponent({
  name: 'MusicLibrary',
  components: {
    IonPage,
    IonContent,
    IonList,
    IonItem,
    IonLabel,
    IonIcon,
    IonRefresher,
    IonRefresherContent,
    IonSpinner,
    PageHeaderWithMenu,
  },
  setup() {
    const globalContext = inject('globalContext') as GlobalContext;
    return {
      globalContext,
      chevronForwardOutline,
    };
  },
  data() {
    return {
      folders: [] as ILibraryMusicFolder[],
      loading: true,
      headerScrolled: false,
    };
  },
  computed: {
    isMobile(): boolean {
      return this.globalContext.isMobile;
    },
  },
  ionViewWillEnter() {
    this.loadFolders();
  },
  methods: {
    onContentScroll(ev: CustomEvent) {
      const t = (ev.detail as { scrollTop?: number }).scrollTop || 0;
      this.headerScrolled = t > 8;
    },
    async loadFolders(ev?: CustomEvent) {
      this.loading = !ev;
      try {
        this.folders = await libraryMusicService.listUserFolders();
      } catch (e) {
        console.error(e);
        this.folders = [];
      } finally {
        this.loading = false;
        if (ev && (ev.target as HTMLIonRefresherElement).complete) {
          (ev.target as HTMLIonRefresherElement).complete();
        }
      }
    },
  },
});
</script>

<style scoped>
.music-library-list {
  background: transparent !important;
  --background: transparent !important;
  padding: 12px 12px 24px;
}

.music-library-folder-item {
  --background: rgba(255, 255, 255, 0.06);
  --background-hover: rgba(255, 255, 255, 0.1);
  --background-activated: rgba(255, 255, 255, 0.12);
  --border-color: rgba(255, 255, 255, 0.1);
  --color: rgba(255, 255, 255, 0.95);
  --padding-start: 16px;
  --padding-end: 12px;
  --inner-padding-end: 4px;
  --min-height: 64px;
  margin-bottom: 10px;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.music-library-folder-item::part(native) {
  border-radius: 14px;
}

.music-library-folder-item ion-label h2 {
  color: #fff !important;
  font-weight: 600;
  font-size: 1.05rem;
}

.music-library-chevron {
  color: var(--ion-color-orange) !important;
  font-size: 1.25rem;
}

.music-library-empty {
  color: rgba(255, 255, 255, 0.55) !important;
  text-align: center;
}

.music-library-spinner {
  display: block;
  margin: 24px auto;
  color: var(--ion-color-orange);
  --color: var(--ion-color-orange);
}

@media (prefers-color-scheme: light) {
  .music-library-folder-item {
    --background: rgba(255, 255, 255, 0.1);
    --background-hover: rgba(255, 255, 255, 0.14);
    --background-activated: rgba(255, 255, 255, 0.16);
    border-color: rgba(255, 255, 255, 0.14);
  }
}
</style>
